import React, { useEffect, useState, useCallback } from "react";
import { SwissDateField } from "@/components/SwissDateInput";
import { useNavigate } from "react-router-dom";
import { useCompany } from "@/context/CompanyContext";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Plus, Banknote, ArrowDownToLine, ArrowUpFromLine, Search, FileText, Trash2, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/formatDate";

function fmtChf(v, currency = "CHF") {
  if (v == null || v === "") return "—";
  return new Intl.NumberFormat("de-CH", { style: "currency", currency }).format(Number(v));
}

const STATUS_BADGE = {
  active: { text: "Aktiv", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  repaid: { text: "Zurückgezahlt", className: "bg-slate-50 text-slate-700 border-slate-200" },
  cancelled: { text: "Storniert", className: "bg-rose-50 text-rose-700 border-rose-200" },
};

const DIRECTION_LABEL = {
  incoming: { text: "Erhalten", icon: ArrowDownToLine, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  outgoing: { text: "Gewährt", icon: ArrowUpFromLine, color: "text-violet-700 bg-violet-50 border-violet-200" },
};

const REPAYMENT_TYPE_LABEL = {
  annuity: "Annuität",
  linear: "Linear",
  bullet: "Endfällig",
};

const FREQ_LABEL = {
  monthly: "monatlich",
  quarterly: "quartalsweise",
  yearly: "jährlich",
  bullet: "endfällig",
};

export default function Loans() {
  const { company } = useCompany();
  const companyId = company?._id;
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetect, setShowDetect] = useState(false);
  const [detectText, setDetectText] = useState("");
  const [detecting, setDetecting] = useState(false);
  const [form, setForm] = useState({
    direction: "incoming",
    counterparty_name: "",
    principal: "",
    currency: "CHF",
    interest_rate_pct: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: "",
    term_months: "",
    repayment_frequency: "monthly",
    repayment_type: "annuity",
    iban: "",
    notes: "",
  });

  const fetchLoans = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await apiClient.get(`/companies/${companyId}/loans`);
      setLoans(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error("Darlehen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);

  async function handleCreate() {
    if (!form.counterparty_name || !form.principal) {
      toast.error("Gegenpartei und Darlehenssumme sind Pflichtfelder");
      return;
    }
    try {
      const body = {
        ...form,
        principal: Number(form.principal),
        interest_rate_pct: Number(form.interest_rate_pct || 0),
        term_months: form.term_months ? Number(form.term_months) : null,
        end_date: form.end_date || null,
        iban: form.iban || null,
        notes: form.notes || null,
      };
      const res = await apiClient.post(`/companies/${companyId}/loans`, body);
      toast.success("Darlehen angelegt");
      setShowCreate(false);
      setForm({
        direction: "incoming", counterparty_name: "", principal: "",
        currency: "CHF", interest_rate_pct: "",
        start_date: new Date().toISOString().slice(0, 10),
        end_date: "", term_months: "",
        repayment_frequency: "monthly", repayment_type: "annuity",
        iban: "", notes: "",
      });
      await fetchLoans();
      navigate(`/loans/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Anlegen");
    }
  }

  async function handleDetect() {
    if (!detectText.trim() || detectText.length < 50) {
      toast.error("Bitte einen Vertragstext mit min. 50 Zeichen einfügen");
      return;
    }
    setDetecting(true);
    try {
      const res = await apiClient.post(
        `/companies/${companyId}/loans/detect-from-text`,
        { text: detectText },
      );
      const { is_loan_contract, confidence, extracted } = res.data;
      if (!is_loan_contract && confidence < 0.5) {
        toast.warning("Der Text wurde nicht als Darlehensvertrag erkannt");
      } else {
        toast.success(`Darlehensvertrag erkannt (Vertrauen ${Math.round(confidence * 100)}%)`);
      }
      // Felder ins Formular vorfüllen
      setForm({
        direction: extracted.direction || "incoming",
        counterparty_name: extracted.counterparty_name || "",
        principal: extracted.principal != null ? String(extracted.principal) : "",
        currency: extracted.currency || "CHF",
        interest_rate_pct: extracted.interest_rate_pct != null ? String(extracted.interest_rate_pct) : "",
        start_date: extracted.start_date || new Date().toISOString().slice(0, 10),
        end_date: extracted.end_date || "",
        term_months: extracted.term_months ? String(extracted.term_months) : "",
        repayment_frequency: extracted.repayment_frequency || "monthly",
        repayment_type: extracted.repayment_type || "annuity",
        iban: extracted.iban || "",
        notes: extracted.notes || "",
      });
      setShowDetect(false);
      setShowCreate(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erkennung fehlgeschlagen");
    } finally {
      setDetecting(false);
    }
  }

  if (!companyId) {
    return <div className="p-6 text-slate-500">Bitte wähle zuerst eine Firma aus.</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="loans-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Banknote className="h-7 w-7 text-emerald-600" />
            Darlehen
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Erhaltene und gewährte Darlehen mit Tilgungsplan und Auto-Buchung.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowDetect(true)}
            data-testid="loans-detect-btn"
          >
            <Search className="h-4 w-4 mr-2" /> Aus Vertragstext erkennen
          </Button>
          <Button onClick={() => setShowCreate(true)} data-testid="loans-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Neues Darlehen
          </Button>
        </div>
      </div>

      <Card className="overflow-hidden" data-testid="loans-table-card">
        <div className="overflow-x-auto">
          <table className="w-full text-base">
            <thead className="bg-slate-50 border-b text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Richtung</th>
                <th className="px-4 py-3">Gegenpartei</th>
                <th className="px-4 py-3 text-right">Darlehenssumme</th>
                <th className="px-4 py-3 text-right">Zinssatz</th>
                <th className="px-4 py-3">Laufzeit</th>
                <th className="px-4 py-3">Tilgung</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-slate-400">Lade …</td>
                </tr>
              ) : loans.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-slate-400">
                    <Banknote className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    Noch keine Darlehen erfasst.
                    <div className="mt-2">
                      <Button variant="link" onClick={() => setShowCreate(true)}>
                        Erstes Darlehen anlegen
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                loans.map((l) => {
                  const dirCfg = DIRECTION_LABEL[l.direction] || {};
                  const DirIcon = dirCfg.icon || ArrowDownToLine;
                  const status = STATUS_BADGE[l.status] || STATUS_BADGE.active;
                  return (
                    <tr
                      key={l.id}
                      className="border-b hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/loans/${l.id}`)}
                      data-testid={`loan-row-${l.id}`}
                    >
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={dirCfg.color}>
                          <DirIcon className="h-3 w-3 mr-1" />
                          {dirCfg.text || l.direction}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 font-medium">{l.counterparty_name}</td>
                      <td className="px-4 py-3 text-right font-mono">
                        {fmtChf(l.principal, l.currency)}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600">
                        {l.interest_rate_pct?.toFixed?.(2) || "0.00"}%
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDate(l.start_date)}
                        {l.end_date && <> – {formatDate(l.end_date)}</>}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {REPAYMENT_TYPE_LABEL[l.repayment_type] || l.repayment_type}
                        {" · "}
                        {FREQ_LABEL[l.repayment_frequency] || l.repayment_frequency}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={status.className}>{status.text}</Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/loans/${l.id}`); }}>
                          <FileText className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create-Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-2xl" data-testid="loans-create-dialog">
          <DialogHeader>
            <DialogTitle>Neues Darlehen erfassen</DialogTitle>
            <DialogDescription>
              Erhaltenes oder gewährtes Darlehen mit Tilgungsplan.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Richtung *</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  variant={form.direction === "incoming" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setForm({ ...form, direction: "incoming" })}
                  data-testid="loan-direction-incoming"
                >
                  <ArrowDownToLine className="h-4 w-4 mr-2" />
                  Wir erhalten ein Darlehen
                </Button>
                <Button
                  variant={form.direction === "outgoing" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setForm({ ...form, direction: "outgoing" })}
                  data-testid="loan-direction-outgoing"
                >
                  <ArrowUpFromLine className="h-4 w-4 mr-2" />
                  Wir gewähren ein Darlehen
                </Button>
              </div>
            </div>
            <div className="col-span-2">
              <Label>{form.direction === "incoming" ? "Darlehensgeber" : "Darlehensnehmer"} *</Label>
              <Input
                value={form.counterparty_name}
                onChange={(e) => setForm({ ...form, counterparty_name: e.target.value })}
                data-testid="loan-counterparty"
              />
            </div>
            <div>
              <Label>Darlehenssumme *</Label>
              <Input
                type="number" step="0.01"
                value={form.principal}
                onChange={(e) => setForm({ ...form, principal: e.target.value })}
                data-testid="loan-principal"
              />
            </div>
            <div>
              <Label>Währung</Label>
              <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
            </div>
            <div>
              <Label>Zinssatz (% p.a.)</Label>
              <Input
                type="number" step="0.01"
                value={form.interest_rate_pct}
                onChange={(e) => setForm({ ...form, interest_rate_pct: e.target.value })}
                data-testid="loan-interest"
              />
            </div>
            <div>
              <Label>Laufzeit (Monate)</Label>
              <Input
                type="number" min="1"
                value={form.term_months}
                onChange={(e) => setForm({ ...form, term_months: e.target.value })}
                data-testid="loan-term"
              />
            </div>
            <div>
              <Label>Startdatum *</Label>
              <SwissDateField
                
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Enddatum (optional)</Label>
              <SwissDateField
                
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Tilgungsart</Label>
              <select
                className="w-full mt-1 border rounded px-2 py-2 text-sm bg-white"
                value={form.repayment_type}
                onChange={(e) => setForm({ ...form, repayment_type: e.target.value })}
                data-testid="loan-repay-type"
              >
                <option value="annuity">Annuität (konstante Rate)</option>
                <option value="linear">Linear (konstante Tilgung)</option>
                <option value="bullet">Endfällig (Zins, Kapital am Ende)</option>
              </select>
            </div>
            <div>
              <Label>Frequenz</Label>
              <select
                className="w-full mt-1 border rounded px-2 py-2 text-sm bg-white"
                value={form.repayment_frequency}
                onChange={(e) => setForm({ ...form, repayment_frequency: e.target.value })}
                data-testid="loan-frequency"
              >
                <option value="monthly">Monatlich</option>
                <option value="quarterly">Quartalsweise</option>
                <option value="yearly">Jährlich</option>
                <option value="bullet">Endfällig</option>
              </select>
            </div>
            <div className="col-span-2">
              <Label>IBAN (optional)</Label>
              <Input value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Notiz (optional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Abbrechen</Button>
            <Button onClick={handleCreate} data-testid="loan-save-btn">Anlegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detect-Dialog */}
      <Dialog open={showDetect} onOpenChange={setShowDetect}>
        <DialogContent className="max-w-2xl" data-testid="loans-detect-dialog">
          <DialogHeader>
            <DialogTitle>Darlehensvertrag durch KI erkennen</DialogTitle>
            <DialogDescription>
              Füge den Vertragstext ein – die KI extrahiert Darlehenssumme, Zinssatz, Laufzeit und Tilgungsart.
              Lade alternativ den Vertrag im <a href="/inbox" className="text-violet-600 underline">Posteingang</a> hoch.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <textarea
              className="w-full min-h-[280px] border rounded p-3 text-sm font-mono"
              placeholder="Hier den vollständigen Vertragstext einfügen ..."
              value={detectText}
              onChange={(e) => setDetectText(e.target.value)}
              data-testid="loan-detect-text"
            />
            <div className="text-xs text-slate-400 mt-1">{detectText.length} Zeichen</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDetect(false)}>Abbrechen</Button>
            <Button onClick={handleDetect} disabled={detecting} data-testid="loan-detect-run-btn">
              <Search className="h-4 w-4 mr-2" />
              {detecting ? "Analysiere ..." : "KI analysieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
